import { AfterRenderRef, Injectable, Injector, afterNextRender } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { LiveAnnouncer } from '@angular/cdk/a11y';


@Injectable({ providedIn: 'root' })
export class BrainTitleStrategy extends TitleStrategy {
  private static readonly productName = 'BRAIN';
  private static readonly mainLandmarkId = 'main-content';
  private static readonly maxFocusRetries = 30;

  private hasNavigated = false;
  /** Bumped on every navigation; a queued focus attempt bails if its captured generation is no
   *  longer current -- belt-and-suspenders alongside destroying the previous retry outright (see
   *  manageRouteChangeFocus/tryClaimFocus). */
  private focusGeneration = 0;
  private pendingFocusRef?: AfterRenderRef;
  /** True once a `Tab` keydown is observed while a focus chain is pending -- see class doc. Reset
   *  whenever a new chain starts. */
  private focusChainInterrupted = false;
  private readonly onKeydownDuringFocusChain = (event: KeyboardEvent): void => {
    if (event.key === 'Tab') {
      this.focusChainInterrupted = true;
    }
  };

  constructor(
    private readonly title: Title,
    private readonly liveAnnouncer: LiveAnnouncer,
    private readonly injector: Injector,
  ) {
    super();
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const routeTitle = this.buildTitle(snapshot);
    this.title.setTitle(routeTitle ? `${routeTitle} - ${BrainTitleStrategy.productName}` : BrainTitleStrategy.productName);
    this.manageRouteChangeFocus(routeTitle);
  }

  private manageRouteChangeFocus(routeTitle: string | undefined): void {
    // A new navigation always supersedes any focus attempt still in flight from the previous one --
    // cancel it outright (not just mark it stale) so an overlapping chain from rapid navigation, or
    // one armed on a route with no landmark at all, can never later latch onto -- and steal focus
    // back to -- a landmark on a screen the user has since left. See class doc.
    this.pendingFocusRef?.destroy();
    this.pendingFocusRef = undefined;
    const generation = ++this.focusGeneration;

    if (!this.hasNavigated) {
      this.hasNavigated = true;
      return;
    }
    if (typeof document === 'undefined') {
      return;
    }


    if (routeTitle) {
      void this.liveAnnouncer.announce(`Navigated to ${routeTitle}`, 'polite');
    }

    document.removeEventListener('keydown', this.onKeydownDuringFocusChain, true);
    this.focusChainInterrupted = false;
    document.addEventListener('keydown', this.onKeydownDuringFocusChain, true);
    this.focusMainLandmark(generation, 0);
  }

  private focusMainLandmark(generation: number, attempt: number): void {
    this.pendingFocusRef = afterNextRender(() => this.tryClaimFocus(generation, attempt), { injector: this.injector });
  }

  /** One attempt in the bounded retry chain. See class doc for the two failure modes and the
   *  cancellation/interruption guards checked below before ever calling `.focus()`. */
  private tryClaimFocus(generation: number, attempt: number): void {
    if (generation !== this.focusGeneration || this.focusChainInterrupted) {
      return;
    }
    const mainEl = document.getElementById(BrainTitleStrategy.mainLandmarkId);
    if (mainEl && mainEl.isConnected) {
      mainEl.focus();
      if (document.activeElement === mainEl) {
        document.removeEventListener('keydown', this.onKeydownDuringFocusChain, true);
        return;
      }
      if (attempt >= BrainTitleStrategy.maxFocusRetries) {
        document.removeEventListener('keydown', this.onKeydownDuringFocusChain, true);
        return;
      }
      // Failure mode 2: found and connected, but focus did not land -- retry on the next animation
      // frame rather than the next render (see class doc for why afterNextRender cannot serve this
      // mode).
      const rafHandle = requestAnimationFrame(() => this.tryClaimFocus(generation, attempt + 1));
      this.pendingFocusRef = { destroy: () => cancelAnimationFrame(rafHandle) };
      return;
    }
    if (attempt >= BrainTitleStrategy.maxFocusRetries) {
      document.removeEventListener('keydown', this.onKeydownDuringFocusChain, true);
      return;
    }
    // Failure mode 1: not mounted yet -- another render is exactly what will mount it.
    this.focusMainLandmark(generation, attempt + 1);
  }
}
